/**
 * Phase 101bk (15/02/2026) — Nayax Cockpit
 *
 * Dashboard 3-tuiles unifié pour flux tendu opérationnel :
 *   1. Ventes par période (aujourd'hui, hier, semaine passée, mois en cours, mois passé)
 *   2. Dernières recharges (10 dernières tournées refill)
 *   3. Alertes désync ventes (ventes Nayax en échec dans les 72h)
 *
 * Consomme :
 *   GET /api/nayax/cockpit/sales-periods
 *   GET /api/nayax/cockpit/recent-refills
 *   GET /api/nayax/cockpit/sync-alerts
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  Package,
  AlertTriangle,
  Calendar,
  Truck,
  ExternalLink,
  ChevronRight,
  Bell,
  CheckCircle2,
  ClipboardList,
  X,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import MomaRefillValidateDialog from "../components/MomaRefillValidateDialog";

const PERIOD_LABELS = {
  today: "Aujourd'hui",
  yesterday: "Hier",
  last_week: "Semaine passée",
  current_month: "Mois en cours",
  previous_month: "Mois passé",
};

function fmtCHF(n) {
  const v = Number(n || 0);
  return `${v.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CHF`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function NayaxCockpit() {
  const [sales, setSales] = useState(null);
  const [refills, setRefills] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [momaRefills, setMomaRefills] = useState(null);
  const [loadingSales, setLoadingSales] = useState(true);
  const [loadingRefills, setLoadingRefills] = useState(true);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [loadingMoma, setLoadingMoma] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [selectedRefill, setSelectedRefill] = useState(null);
  // Phase 101cu (Feb 2026) — Actions groupées sur les refills pending.
  // Phase 101fj (Feb 2026) — Purge FIFO auto : le bulk-auto-fifo est retiré.
  // Seul le bulk-dismiss reste. La validation doit être manuelle refill par
  // refill (choix explicite des lots — libre arbitre opérateur).
  const [selectedRefillIds, setSelectedRefillIds] = useState(new Set());
  const [bulkDismissBusy, setBulkDismissBusy] = useState(false);
  const [bulkDismissOpen, setBulkDismissOpen] = useState(false);
  const [bulkDismissReason, setBulkDismissReason] = useState("");

  const loadSales = useCallback(async () => {
    setLoadingSales(true);
    try {
      const { data } = await api.get("/nayax/cockpit/sales-periods");
      setSales(data);
    } catch (e) {
      toast.error("Erreur chargement ventes : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoadingSales(false);
    }
  }, []);

  const loadRefills = useCallback(async () => {
    setLoadingRefills(true);
    try {
      const { data } = await api.get("/nayax/cockpit/recent-refills?limit=10");
      setRefills(data);
    } catch (e) {
      toast.error("Erreur chargement recharges : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoadingRefills(false);
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    try {
      const { data } = await api.get("/nayax/cockpit/sync-alerts?limit=20&since_hours=72");
      setAlerts(data);
    } catch (e) {
      toast.error("Erreur chargement alertes : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  const loadMomaRefills = useCallback(async () => {
    setLoadingMoma(true);
    try {
      const { data } = await api.get("/moma-refills/pending?limit=50");
      setMomaRefills(data);
    } catch (e) {
      toast.error("Erreur chargement refills MOMA : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoadingMoma(false);
    }
  }, []);

  const runDetection = async () => {
    setDetecting(true);
    try {
      const { data } = await api.post("/moma-refills/detect");
      if (data.total_created > 0) {
        toast.success(`${data.total_created} nouveau(x) refill(s) MOMA détecté(s)`);
      } else {
        toast.info("Aucun nouveau refill détecté");
      }
      loadMomaRefills();
    } catch (e) {
      toast.error("Erreur détection : " + (e?.response?.data?.detail || e.message));
    } finally {
      setDetecting(false);
    }
  };

  const runScanClosedTours = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/moma-refills/scan-closed-tours?days=10");
      if (data.total_created > 0) {
        toast.success(`${data.total_created} tournée(s) sans DLC ajoutée(s) au rattrapage`);
      } else {
        toast.info(`Aucune tournée sans DLC détectée sur les ${data.days} derniers jours (${data.tours_scanned} scannée(s))`);
      }
      loadMomaRefills();
    } catch (e) {
      toast.error("Erreur rattrapage : " + (e?.response?.data?.detail || e.message));
    } finally {
      setScanning(false);
    }
  };

  // Phase 101cu — Sélection multiple + actions groupées
  const toggleRefillSelected = (id) => {
    setSelectedRefillIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllPending = (pendingIds) => {
    setSelectedRefillIds((prev) => {
      if (prev.size === pendingIds.length && pendingIds.every((id) => prev.has(id))) return new Set();
      return new Set(pendingIds);
    });
  };

  // Phase 101fj (Feb 2026) — runBulkFifo retiré (purge FIFO auto totale)

  const openBulkDismiss = () => {
    if (selectedRefillIds.size === 0) return;
    setBulkDismissReason("");
    setBulkDismissOpen(true);
  };

  const confirmBulkDismiss = async () => {
    const ids = Array.from(selectedRefillIds);
    const reason = bulkDismissReason.trim();
    if (!reason) {
      toast.error("Motif obligatoire");
      return;
    }
    setBulkDismissBusy(true);
    try {
      const { data } = await api.post("/moma-refills/bulk-dismiss", {
        refill_ids: ids,
        reason,
      });
      toast.success(`${data.dismissed} refill(s) rejeté(s) (${data.skipped} sauté(s))`);
      setSelectedRefillIds(new Set());
      setBulkDismissOpen(false);
      loadMomaRefills();
    } catch (e) {
      toast.error("Erreur bulk dismiss : " + (e?.response?.data?.detail || e.message));
    } finally {
      setBulkDismissBusy(false);
    }
  };

  useEffect(() => {
    loadSales();
    loadRefills();
    loadAlerts();
    loadMomaRefills();
  }, [loadSales, loadRefills, loadAlerts, loadMomaRefills]);

  const refreshAll = () => {
    loadSales();
    loadRefills();
    loadAlerts();
    loadMomaRefills();
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto" data-testid="nayax-cockpit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-medium text-[#1A1C1A]">Cockpit Nayax</h1>
          <p className="text-sm text-[#575A57] mt-1">
            Vue unifiée pour flux tendu — ventes, recharges, alertes désync.
          </p>
        </div>
        <Button
          onClick={refreshAll}
          disabled={loadingSales || loadingRefills || loadingAlerts}
          variant="outline"
          className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#E8F2EC]"
          data-testid="cockpit-refresh-btn"
        >
          {loadingSales || loadingRefills || loadingAlerts ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Rafraîchir
        </Button>
      </div>

      {/* Tuile 1 — Ventes par période */}
      <SalesPeriodsTile data={sales} loading={loadingSales} />

      {/* Tuile 4 — Refills MOMA en attente de DLC (mise en évidence) */}
      <MomaRefillsTile
        data={momaRefills}
        loading={loadingMoma}
        detecting={detecting}
        scanning={scanning}
        onDetect={runDetection}
        onScanClosed={runScanClosedTours}
        onValidate={setSelectedRefill}
        selectedIds={selectedRefillIds}
        onToggleSelected={toggleRefillSelected}
        onToggleAll={selectAllPending}
        onBulkDismiss={openBulkDismiss}
      />

      {/* Tuile 2 + 3 côte à côte sur large */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <RecentRefillsTile data={refills} loading={loadingRefills} />
        <SyncAlertsTile data={alerts} loading={loadingAlerts} />
      </div>

      <MomaRefillValidateDialog
        open={!!selectedRefill}
        refill={selectedRefill}
        onClose={() => setSelectedRefill(null)}
        onValidated={loadMomaRefills}
      />

      {/* Phase 101cu — Dialog bulk dismiss (motif commun obligatoire) */}
      <Dialog open={bulkDismissOpen} onOpenChange={setBulkDismissOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-dismiss-dialog">
          <DialogHeader>
            <DialogTitle>Rejeter {selectedRefillIds.size} refill(s) groupé(s)</DialogTitle>
            <DialogDescription>
              Motif commun enregistré sur chaque refill (audit). Aucun mouvement de stock n&apos;est créé.
              Typiques : « Produit non remappé Nayax », « Resync doublon », « Ventes déjà retracées ailleurs ».
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <Label className="text-xs text-[#575A57]">
              Motif <span className="text-[#C05C47]">*</span>
            </Label>
            <Textarea
              value={bulkDismissReason}
              onChange={(e) => setBulkDismissReason(e.target.value)}
              placeholder="ex : Produit non remappé Nayax Sierre — remap fait le 12/02/2026"
              maxLength={500}
              rows={3}
              data-testid="bulk-dismiss-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDismissOpen(false)} disabled={bulkDismissBusy}>
              Annuler
            </Button>
            <Button
              className="bg-[#7A1F1F] hover:bg-[#611818] text-white"
              onClick={confirmBulkDismiss}
              disabled={bulkDismissBusy || !bulkDismissReason.trim()}
              data-testid="bulk-dismiss-confirm"
            >
              {bulkDismissBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
              Rejeter les {selectedRefillIds.size} refill(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// TUILE 1 — Ventes par période
// ============================================================
function SalesPeriodsTile({ data, loading }) {
  const periods = data?.periods || {};
  const asOf = data?.as_of;

  return (
    <Card className="bg-white border-[#E5E5E0]" data-testid="tile-sales-periods">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#1F4E3D]" />
            <h2 className="text-lg font-medium text-[#1A1C1A]">💰 Ventes par période</h2>
          </div>
          {asOf && (
            <span className="text-xs text-[#8C8C88]">
              Actualisé {fmtDate(asOf)} · {data?.tz}
            </span>
          )}
        </div>

        {loading ? (
          <div className="py-12 text-center text-[#8C8C88]" data-testid="tile-sales-loading">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Chargement...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {["today", "yesterday", "last_week", "current_month", "previous_month"].map((key) => (
              <PeriodCard
                key={key}
                label={PERIOD_LABELS[key]}
                data={periods[key]}
                highlight={key === "today"}
                testid={`period-${key}`}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PeriodCard({ label, data, highlight, testid }) {
  if (!data) return null;
  const { revenue, count, by_machine, avg_per_day, projection, days_elapsed, days_total } = data;

  const bg = highlight ? "bg-[#E8F2EC]" : "bg-[#FAFAF7]";
  const border = highlight ? "border-[#1F4E3D]" : "border-[#E5E5E0]";
  const revenueColor = highlight ? "text-[#1F4E3D]" : "text-[#1A1C1A]";

  return (
    <div
      className={`${bg} ${border} border rounded-lg p-4 flex flex-col gap-2`}
      data-testid={testid}
    >
      <div className="text-xs uppercase tracking-wider text-[#8C8C88]">{label}</div>
      <div className={`text-2xl font-medium font-mono ${revenueColor}`}>
        {fmtCHF(revenue)}
      </div>
      <div className="text-xs text-[#575A57]">
        {count} vente{count !== 1 ? "s" : ""}
      </div>
      {avg_per_day !== undefined && (
        <div className="text-[11px] text-[#8C8C88] pt-1 border-t border-[#E5E5E0]">
          Moy/jour : {fmtCHF(avg_per_day)}
        </div>
      )}
      {projection !== undefined && (
        <div className="text-[11px] text-[#8C8C88] pt-1 border-t border-[#E5E5E0]">
          <div>
            <TrendingUp className="w-3 h-3 inline mr-1" /> Projection : {fmtCHF(projection)}
          </div>
          <div className="text-[10px] text-[#A3A6A3]">
            J{days_elapsed}/{days_total}
          </div>
        </div>
      )}
      {by_machine?.length > 0 && (
        <details className="text-[11px] text-[#575A57] mt-1">
          <summary className="cursor-pointer hover:text-[#1F4E3D]">
            Par machine ({by_machine.length})
          </summary>
          <div className="mt-2 space-y-1">
            {by_machine.map((m) => (
              <div key={m.distributor_id} className="flex justify-between gap-1">
                <span className="truncate flex-1" title={m.distributor_name}>
                  {m.distributor_name}
                </span>
                <span className="font-mono text-[#1A1C1A]">{fmtCHF(m.revenue)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ============================================================
// TUILE 2 — Dernières recharges
// ============================================================
function RecentRefillsTile({ data, loading }) {
  const tours = data?.tours || [];

  const statusStyle = (st) => {
    const map = {
      BROUILLON: "bg-[#F4F4F0] text-[#575A57]",
      EN_PREPA: "bg-[#FBF4E9] text-[#8C6B2F]",
      EN_LIVRAISON: "bg-[#E8F2EC] text-[#1F4E3D]",
      CLOTUREE: "bg-[#E8EDE8] text-[#575A57]",
    };
    return map[st] || "bg-[#F4F4F0] text-[#575A57]";
  };

  return (
    <Card className="bg-white border-[#E5E5E0]" data-testid="tile-recent-refills">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#1F4E3D]" />
            <h2 className="text-lg font-medium text-[#1A1C1A]">📦 Dernières recharges</h2>
          </div>
          <Link to="/tournees-reappro">
            <Button variant="ghost" size="sm" className="text-[#1F4E3D] hover:bg-[#E8F2EC] h-8">
              Toutes <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="py-8 text-center text-[#8C8C88]" data-testid="tile-refills-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement...
          </div>
        ) : tours.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#8C8C88]" data-testid="tile-refills-empty">
            Aucune tournée récente.
          </div>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {tours.map((t) => (
              <Link
                key={t.id}
                to={`/tournees-reappro/${t.id}`}
                className="block border border-[#E5E5E0] rounded-md p-3 hover:bg-[#FDFDFB] transition-colors"
                data-testid={`refill-item-${t.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-medium text-[#1A1C1A]">
                        {t.reference || "—"}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${statusStyle(t.status)}`}>
                        {t.status}
                      </span>
                    </div>
                    <div className="text-xs text-[#575A57] mt-1 truncate">
                      {t.distributors_names?.join(" · ") || "(aucun distributeur)"}
                    </div>
                    <div className="text-[11px] text-[#8C8C88] mt-1">
                      {t.created_by_name || "?"} · {fmtDateShort(t.created_at)} · {t.total_loaded} chargés / {t.total_planned} prévus
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-[#8C8C88] flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// TUILE 3 — Alertes désync ventes
// ============================================================
function SyncAlertsTile({ data, loading }) {
  const alerts = data?.alerts || [];

  return (
    <Card className="bg-white border-[#E5E5E0]" data-testid="tile-sync-alerts">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#B93A25]" />
            <h2 className="text-lg font-medium text-[#1A1C1A]">
              ⚠ Alertes désync ventes
              {alerts.length > 0 && (
                <span className="ml-2 text-xs bg-[#FFEDEA] text-[#B93A25] px-2 py-0.5 rounded-full">
                  {alerts.length}
                </span>
              )}
            </h2>
          </div>
          <Link to="/parametres/nayax-sync-logs">
            <Button variant="ghost" size="sm" className="text-[#1F4E3D] hover:bg-[#E8F2EC] h-8">
              Historique <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="text-[11px] text-[#8C8C88] mb-3">
          Fenêtre : 72 dernières heures · Source : syncs Nayax en échec (ventes non enregistrées).
        </div>

        {loading ? (
          <div className="py-8 text-center text-[#8C8C88]" data-testid="tile-alerts-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement...
          </div>
        ) : alerts.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#1F4E3D]" data-testid="tile-alerts-empty">
            ✅ Aucune alerte sur les 72 dernières heures
          </div>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {alerts.map((a) => (
              <div
                key={a.id}
                className="border border-[#F0C8BF] bg-[#FFF8F5] rounded-md p-3"
                data-testid={`alert-item-${a.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium text-[#1A1C1A] truncate">
                    {a.distributor_name}
                  </span>
                  <span className="text-[11px] text-[#8C8C88] flex-shrink-0">
                    {fmtDate(a.created_at)}
                  </span>
                </div>
                <div className="text-xs text-[#B93A25] mb-1">
                  <TrendingDown className="w-3 h-3 inline mr-1" />
                  {a.failed} échec{a.failed > 1 ? "s" : ""} sur {a.transactions_received} tx reçues
                  {a.sales_created > 0 && (
                    <span className="text-[#575A57]"> · {a.sales_created} OK</span>
                  )}
                </div>
                {a.errors?.length > 0 && (
                  <details className="text-[11px] text-[#575A57] mt-2">
                    <summary className="cursor-pointer hover:text-[#B93A25]">
                      Détail ({a.errors.length} erreur{a.errors.length > 1 ? "s" : ""})
                    </summary>
                    <ul className="mt-1 space-y-0.5 list-disc list-inside">
                      {a.errors.slice(0, 5).map((err, i) => (
                        <li key={i} className="break-words">{err}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ============================================================
// TUILE 4 — Refills MOMA en attente de DLC (Phase 101bl)
// Phase 101cu (Feb 2026) — Actions groupées : rejet groupé (motif commun)
// Phase 101fj (Feb 2026) — Purge FIFO auto : bouton FIFO retiré, la
// validation doit rester manuelle refill par refill (libre arbitre).
// ============================================================
function MomaRefillsTile({
  data, loading, detecting, onDetect, onScanClosed, scanning, onValidate,
  selectedIds = new Set(), onToggleSelected = () => {}, onToggleAll = () => {},
  onBulkDismiss = () => {},
}) {
  const pending = data?.pending || [];
  const hasPending = pending.length > 0;
  const pendingIds = pending.map((r) => r.id);
  const selectedCount = selectedIds.size;
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selectedIds.has(id));

  const SOURCE_META = {
    moma_delta: { label: "MOMA delta", color: "bg-[#E8F2EC] text-[#1F4E3D]", icon: "📊", title: "Detecté par comparaison snapshots N vs N-1" },
    orphan_sale: { label: "Vente orpheline", color: "bg-[#FFF8F5] text-[#B93A25]", icon: "⚠", title: "Vente Nayax sans lot AM (insufficient_stock)" },
    closed_tour: { label: "Tournée sans DLC", color: "bg-[#FBF4E9] text-[#8C6B2F]", icon: "📋", title: "Ligne de tournée clôturée sans DLC saisie (rattrapage)" },
  };

  return (
    <Card
      className={`border-2 ${hasPending ? "bg-[#FFF8F0] border-[#E8B94B]" : "bg-white border-[#E5E5E0]"}`}
      data-testid="tile-moma-refills"
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell className={`w-5 h-5 ${hasPending ? "text-[#E8B94B]" : "text-[#8C8C88]"}`} />
            <h2 className="text-lg font-medium text-[#1A1C1A]">
              🔔 Refills à compléter (DLC)
              {hasPending && (
                <span
                  className="ml-2 text-xs bg-[#E8B94B] text-white px-2 py-0.5 rounded-full font-bold"
                  data-testid="moma-refills-badge"
                >
                  {pending.length}
                </span>
              )}
            </h2>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onScanClosed}
              disabled={scanning || loading}
              className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9] h-8"
              title="Scan les tournées CLOTUREE des 10 derniers jours et détecte les lignes sans DLC"
              data-testid="scan-closed-tours-btn"
            >
              {scanning ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              ) : (
                <ClipboardList className="w-3.5 h-3.5 mr-1" />
              )}
              Rattrapage J-10
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDetect}
              disabled={detecting || loading}
              className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#E8F2EC] h-8"
              data-testid="moma-detect-btn"
            >
              {detecting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
              )}
              Détecter delta
            </Button>
          </div>
        </div>

        <div className="text-[11px] text-[#8C8C88] mb-3">
          3 sources unifiées : delta snapshots MOMA, ventes orphelines (insufficient_stock), tournées clôturées sans DLC (rattrapage J-10).
        </div>

        {/* Phase 101cu — Barre d'actions groupées (visible si au moins 1 pending) */}
        {hasPending && (
          <div className="flex items-center gap-2 flex-wrap bg-[#FAF6E8] border border-[#E8B94B]/40 rounded px-3 py-2 mb-3" data-testid="moma-bulk-toolbar">
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => onToggleAll(pendingIds)}
                className="w-4 h-4 accent-[#1F4E3D]"
                data-testid="moma-bulk-select-all"
              />
              <span className="text-[#575A57]">
                {selectedCount > 0
                  ? <><b className="text-[#1A1C1A]">{selectedCount}</b> sélectionné{selectedCount > 1 ? "s" : ""} sur {pendingIds.length}</>
                  : <>Sélectionner tout ({pendingIds.length})</>}
              </span>
            </label>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              onClick={onBulkDismiss}
              disabled={selectedCount === 0}
              className="h-8 border-[#7A1F1F] text-[#7A1F1F] hover:bg-[#F9EAEA]"
              data-testid="moma-bulk-dismiss-btn"
              title="Rejeter les refills sélectionnés en un clic avec un motif commun"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Rejeter tout ({selectedCount})
            </Button>
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-[#8C8C88]" data-testid="tile-moma-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement...
          </div>
        ) : !hasPending ? (
          <div className="py-6 text-center text-sm text-[#1F4E3D] flex items-center justify-center gap-2" data-testid="tile-moma-empty">
            <CheckCircle2 className="w-4 h-4" />
            Aucun refill à compléter — traçabilité HACCP à jour.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[500px] overflow-y-auto">
            {pending.map((r) => {
              const meta = SOURCE_META[r.source] || SOURCE_META.moma_delta;
              const isSelected = selectedIds.has(r.id);
              return (
                <div
                  key={r.id}
                  className={`relative border rounded-md p-3 transition-all ${isSelected ? "border-[#1F4E3D] bg-[#F0F7F3] shadow-sm" : "border-[#E5E5E0] bg-white hover:border-[#1F4E3D] hover:shadow-sm"}`}
                  data-testid={`moma-refill-item-${r.id}`}
                >
                  {/* Checkbox de sélection (bulk) — absolutely positioned, no propagation */}
                  <label
                    className="absolute top-2 right-2 flex items-center cursor-pointer p-1"
                    onClick={(e) => e.stopPropagation()}
                    title="Sélectionner pour les actions groupées"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelected(r.id)}
                      className="w-4 h-4 accent-[#1F4E3D]"
                      data-testid={`moma-refill-select-${r.id}`}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => onValidate(r)}
                    className="text-left w-full pr-6"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1 pr-4">
                      <span className="text-sm font-medium text-[#1A1C1A] truncate flex-1">
                        {r.distributor_name}
                      </span>
                      <span
                        className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.color} flex-shrink-0`}
                        title={meta.title}
                      >
                        {meta.icon} {meta.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-[#8C8C88] mb-1">
                      {r.slot_code && (
                        <span className="bg-[#F4F4F0] px-1.5 py-0.5 rounded">Slot {r.slot_code}</span>
                      )}
                      {r.source_orphan_tx_ids?.length > 0 && (
                        <span>· {r.source_orphan_tx_ids.length} tx cumulée{r.source_orphan_tx_ids.length > 1 ? "s" : ""}</span>
                      )}
                      {r.source_tour_id && <span>· tournée {r.source_tour_id.slice(0, 8)}</span>}
                    </div>
                    <div className="text-xs text-[#575A57] truncate mb-1 flex items-center gap-1">
                      <span className="truncate">
                        {r.product_name || <span className="italic text-[#B93A25]">Produit non identifié</span>}
                      </span>
                      {r.stock_managed === false && (
                        <span className="text-[9px] uppercase tracking-wider bg-[#E8F2EC] text-[#1F4E3D] px-1 rounded flex-shrink-0" title="Stock simple (boisson/snack) — pas de DLC">
                          Boisson
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-[#8C8C88]">{fmtDate(r.detected_at)}</span>
                      <span className="text-[#1F4E3D] font-medium">
                        +{r.qty_detected} {r.qty_detected > 1 ? "unités" : "unité"}
                      </span>
                    </div>
                    {r.sales_between > 0 && (
                      <div className="text-[10px] text-[#8C6B2F] mt-0.5">
                        (dont {r.sales_between} vente{r.sales_between > 1 ? "s" : ""} compensée{r.sales_between > 1 ? "s" : ""})
                      </div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
